<script setup>
import { ref, reactive } from 'vue';
import axios from 'axios';

const props = defineProps({
    initialModels: { type: Array, required: true },
    storeUrl: { type: String, required: true },
    updateUrlTemplate: { type: String, required: true },
});

const models = ref([...props.initialModels]);
const saving = ref(false);
const error = ref('');

const emptyForm = () => ({
    provider: '',
    model: '',
    input_price_per_million: '',
    output_price_per_million: '',
    is_active: true,
});

const newModel = reactive(emptyForm());
const editingId = ref(null);
const editForm = reactive(emptyForm());

function startEdit(m) {
    editingId.value = m.id;
    Object.assign(editForm, {
        provider: m.provider,
        model: m.model,
        input_price_per_million: m.input_price_per_million,
        output_price_per_million: m.output_price_per_million,
        is_active: m.is_active,
    });
}

function cancelEdit() {
    editingId.value = null;
}

async function saveNew() {
    saving.value = true;
    error.value = '';
    try {
        const { data } = await axios.post(props.storeUrl, {
            provider: newModel.provider,
            model: newModel.model,
            input_price_per_million: parseFloat(newModel.input_price_per_million),
            output_price_per_million: parseFloat(newModel.output_price_per_million),
        });
        models.value.push(data);
        Object.assign(newModel, emptyForm());
    } catch (e) {
        error.value = e.response?.data?.message || 'Failed to save';
    } finally {
        saving.value = false;
    }
}

async function saveEdit(id) {
    saving.value = true;
    error.value = '';
    try {
        const url = props.updateUrlTemplate.replace('__ID__', id);
        const { data } = await axios.put(url, {
            provider: editForm.provider,
            model: editForm.model,
            input_price_per_million: parseFloat(editForm.input_price_per_million),
            output_price_per_million: parseFloat(editForm.output_price_per_million),
            is_active: editForm.is_active,
        });
        const idx = models.value.findIndex(m => m.id === id);
        if (idx !== -1) models.value[idx] = data;
        editingId.value = null;
    } catch (e) {
        error.value = e.response?.data?.message || 'Failed to update';
    } finally {
        saving.value = false;
    }
}

async function toggleActive(m) {
    const url = props.updateUrlTemplate.replace('__ID__', m.id);
    try {
        const { data } = await axios.put(url, {
            provider: m.provider,
            model: m.model,
            input_price_per_million: m.input_price_per_million,
            output_price_per_million: m.output_price_per_million,
            is_active: !m.is_active,
        });
        const idx = models.value.findIndex(x => x.id === m.id);
        if (idx !== -1) models.value[idx] = data;
    } catch (e) {
        error.value = e.response?.data?.message || 'Failed to toggle';
    }
}
</script>

<template>
    <div class="max-w-5xl mx-auto">
        <h1 class="text-2xl font-bold text-gray-900 mb-6">AI Model Pricing</h1>

        <div v-if="error" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {{ error }}
            <button @click="error = ''" class="ml-2 text-red-500 hover:text-red-700">&times;</button>
        </div>

        <div class="bg-white shadow rounded-lg overflow-hidden">
            <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                    <tr>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Provider</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
                        <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Input $/M</th>
                        <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Output $/M</th>
                        <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Active</th>
                        <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-gray-200">
                    <!-- Existing models -->
                    <tr v-for="m in models" :key="m.id" :class="{ 'bg-gray-50': !m.is_active }">
                        <template v-if="editingId === m.id">
                            <td class="px-4 py-2">
                                <input v-model="editForm.provider" class="w-full border rounded px-2 py-1 text-sm" />
                            </td>
                            <td class="px-4 py-2">
                                <input v-model="editForm.model" class="w-full border rounded px-2 py-1 text-sm" />
                            </td>
                            <td class="px-4 py-2">
                                <input v-model="editForm.input_price_per_million" type="number" step="0.000001" class="w-24 border rounded px-2 py-1 text-sm text-right" />
                            </td>
                            <td class="px-4 py-2">
                                <input v-model="editForm.output_price_per_million" type="number" step="0.000001" class="w-24 border rounded px-2 py-1 text-sm text-right" />
                            </td>
                            <td class="px-4 py-2 text-center">
                                <input v-model="editForm.is_active" type="checkbox" class="rounded" />
                            </td>
                            <td class="px-4 py-2 text-right space-x-2">
                                <button @click="saveEdit(m.id)" :disabled="saving" class="text-green-600 hover:text-green-800 text-sm font-medium">Save</button>
                                <button @click="cancelEdit" class="text-gray-500 hover:text-gray-700 text-sm">Cancel</button>
                            </td>
                        </template>
                        <template v-else>
                            <td class="px-4 py-2 text-sm text-gray-900">{{ m.provider }}</td>
                            <td class="px-4 py-2 text-sm font-mono text-gray-900">{{ m.model }}</td>
                            <td class="px-4 py-2 text-sm text-right text-gray-700">{{ m.input_price_per_million }}</td>
                            <td class="px-4 py-2 text-sm text-right text-gray-700">{{ m.output_price_per_million }}</td>
                            <td class="px-4 py-2 text-center">
                                <button @click="toggleActive(m)" :class="m.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'" class="text-xs px-2 py-1 rounded-full font-medium">
                                    {{ m.is_active ? 'Active' : 'Inactive' }}
                                </button>
                            </td>
                            <td class="px-4 py-2 text-right">
                                <button @click="startEdit(m)" class="text-blue-600 hover:text-blue-800 text-sm font-medium">Edit</button>
                            </td>
                        </template>
                    </tr>

                    <!-- Add new row -->
                    <tr class="bg-blue-50">
                        <td class="px-4 py-2">
                            <input v-model="newModel.provider" placeholder="anthropic" class="w-full border rounded px-2 py-1 text-sm" />
                        </td>
                        <td class="px-4 py-2">
                            <input v-model="newModel.model" placeholder="claude-sonnet-4-20250514" class="w-full border rounded px-2 py-1 text-sm" />
                        </td>
                        <td class="px-4 py-2">
                            <input v-model="newModel.input_price_per_million" type="number" step="0.000001" placeholder="3.00" class="w-24 border rounded px-2 py-1 text-sm text-right" />
                        </td>
                        <td class="px-4 py-2">
                            <input v-model="newModel.output_price_per_million" type="number" step="0.000001" placeholder="15.00" class="w-24 border rounded px-2 py-1 text-sm text-right" />
                        </td>
                        <td class="px-4 py-2"></td>
                        <td class="px-4 py-2 text-right">
                            <button @click="saveNew" :disabled="saving || !newModel.provider || !newModel.model" class="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm px-3 py-1 rounded">
                                Add
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>
